/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, { useCallback, useEffect, useRef } from "react";
import { cn } from "@kit/ui/utils";
import { MarkdownRenderer } from "./markdown-renderer";
import { useTranslation } from "react-i18next";
import { UIMessage } from "@ai-sdk/react";
import { MessageContentType, MessageSchema } from "~/lib/schemas/MessageSchema.schema";
import { SecureGeneralContentsArgsSchema, GeneralContentSchema, GeneralContentsContentType } from "~/lib/schemas/generalContentSchema.schema";
import { HorizontalProductsComponent } from "./horizontal-products-component";
import { HighlightLinksComponent } from "./highlight-links-component";
import { JSONValue } from "ai";


interface MessagesProps {
  messages: UIMessage[];
  error?: Error | null;
  isShowSimple?: boolean;
}

export function Messages({ messages, error, isShowSimple }: MessagesProps) {
  const { t } = useTranslation('teams');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // TODO: block should end on messages, and remove block when on client chat
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: isShowSimple ? "end" : undefined });
  }, [messages, isShowSimple]);

   // Type guard to check if value is JSONValue
  const isJSONValue = (value: unknown): value is JSONValue => {
    return value !== undefined && 
           (value === null || 
            typeof value === 'string' || 
            typeof value === 'number' || 
            typeof value === 'boolean' || 
            Array.isArray(value) || 
            (typeof value === 'object' && value !== null));
  };

  // TODO: `isShowSimple` just a workaround to hide on /messages page because can't scroll horizontal. Please fix and remove the workaround.
  const contentRenderer = useCallback((contentType: MessageContentType | GeneralContentsContentType, content: any): React.ReactNode => {
    switch (contentType) {
      case "products":
      case "product": {
        if (Array.isArray(content)) {
          const contents = content.map(item => {
            return GeneralContentSchema.parse(item);
          })
          return isShowSimple ? <div>(Showing contents)</div> : <HorizontalProductsComponent contents={contents}/>
        } else {
          return null;
        }
      }
      case "links":
      case "link": {
        if (Array.isArray(content)) {
          const contents = content.map(item => {
            return GeneralContentSchema.parse(item);
          })
          return isShowSimple ? <div>(Showing contents)</div> : <HighlightLinksComponent contents={contents}/>
        } else {
          return null;
        }
      }
      default: 
        return null;
    }
  }, [isShowSimple]);
  
  const renderToolContent = useCallback((parts: any[]): React.ReactNode => {
    return parts?.map((part: any, index: number) => {
      if (part.type && part.type.startsWith('tool-') && part.output && (
        part.type === 'tool-getProductsInformation' ||
        part.type === 'tool-getLinksInformation'
      )) {
        const args = SecureGeneralContentsArgsSchema.parse(part.input)
        const rendered = contentRenderer(args.contentType, part.output);
        return <div key={index}>{rendered}</div>
      }
      return null;
    }).filter(Boolean);
  }, [contentRenderer]);

  const renderChatContent = useCallback((value: any): React.ReactNode => {
    if (!isJSONValue(value)) return;

    const message = MessageSchema.parse(value)
    if (message.content && message.content_type) {
      const messageContent = message.content;
      let parsedJson;
      try {
        parsedJson = JSON.parse(messageContent);
        return contentRenderer(message.content_type, parsedJson);
      } catch (error) {
        console.error("Error parsing JSON contents:", error);
        return null;
      }
    } else {
      return null;
    }
  }, [contentRenderer]);

  return (
    <div className='flex-1 flex-col overflow-auto pt-4 pb-10'>
      <div className="container max-w-4xl space-y-2">
        {messages.map((message, index) => (
          <div key={`${message.id}-${index}`} className="space-y-1">
              <div 
              className={cn(
                "flex w-fit max-w-[90%] flex-col gap-2 rounded-lg px-3 py-2 text-base break-words",
                message.role === "user"
                ? "ml-auto bg-primary text-primary-foreground"
                : "bg-muted"
              )}
              >
              <MarkdownRenderer markdown={message.parts?.find(part => part.type === 'text')?.text || ''}/>
              </div>
            {message.metadata ? renderChatContent(message.metadata) : null}
            {message.parts ? renderToolContent(message.parts) : null}
          </div>
        ))}
        { error && 
          <div className={cn("flex w-fit max-w-[90%] flex-col gap-2 rounded-lg px-3 py-2 text-base break-words", "bg-muted")}>
            { t("chatErrorMessage") }
          </div>
        }
      </div>
      <div ref={messagesEndRef} />
    </div>
  );
}